import { useState } from 'react'
import { PermissionButton } from '../components/Permissions.js'
import { useNavigate } from 'react-router-dom'
import { useProject } from '../components/ProjectLayout.js'
import { CopyButton, Dialog, ErrorBox, PageHeader, StatusPill } from '../components/ui.js'
import { useApi } from '../lib/useApi.js'

export default function ProjectSettings() {
  const { project: p, reload } = useProject()
  const api = useApi()
  const navigate = useNavigate()
  const [action, setAction] = useState<'pause' | 'resume' | 'delete' | null>(null)
  const [confirmation, setConfirmation] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <>
      <PageHeader
        title="Project settings"
        description="Project identity, availability and lifecycle controls."
      />
      <section className="card panel">
        <h2>General</h2>
        <dl className="settings-list">
          <div>
            <dt>Name</dt>
            <dd>{p.name}</dd>
          </div>
          <div>
            <dt>Project ID</dt>
            <dd>
              <code>{p.id}</code> <CopyButton value={p.id} />
            </dd>
          </div>
          <div>
            <dt>Database</dt>
            <dd>{p.databaseTier.toUpperCase()}</dd>
          </div>
          <div>
            <dt>Region</dt>
            <dd>{p.region}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <StatusPill status={p.status} />
            </dd>
          </div>
        </dl>
      </section>
      <section className="card panel">
        <h2>Project availability</h2>
        <p className="sub">
          Pausing stops new requests after propagation. Your data is retained and storage may still incur
          charges. Running work is not cancelled.
        </p>
        {['active', 'paused'].includes(p.status) && (
          <PermissionButton
            permission="project:update"
            className="btn"
            onClick={() => {
              setError(null)
              setAction(p.status === 'paused' ? 'resume' : 'pause')
            }}
          >
            {p.status === 'paused' ? 'Resume project' : 'Pause project'}
          </PermissionButton>
        )}
      </section>
      <section className="card panel danger-zone">
        <h2>Danger zone</h2>
        <p>
          Permanently delete this project and its provisioned database, storage, cache and recorded Workers.
          This cannot be undone.
        </p>
        <PermissionButton
          permission="project:delete"
          className="btn btn-danger"
          onClick={() => {
            setConfirmation('')
            setError(null)
            setAction('delete')
          }}
        >
          Delete project…
        </PermissionButton>
      </section>
      <Dialog
        open={action !== null}
        title={
          action === 'delete'
            ? 'Delete project permanently'
            : action === 'pause'
              ? 'Pause project'
              : 'Resume project'
        }
        busy={busy}
        onClose={() => setAction(null)}
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault()
            if (!action || busy || (action === 'delete' && confirmation !== p.name)) return
            setBusy(true)
            setError(null)
            try {
              if (action === 'delete') {
                await api.deleteProject(p.id)
                navigate('/projects')
              } else {
                await (action === 'pause' ? api.pauseProject(p.id) : api.resumeProject(p.id))
                setAction(null)
                reload()
              }
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Action failed. Please retry.')
            } finally {
              setBusy(false)
            }
          }}
        >
          <p>
            {action === 'delete'
              ? 'The project’s provisioned resources will be permanently removed. Deletion cannot be recovered.'
              : action === 'pause'
                ? 'New requests will be refused after propagation. Data is kept; running work and storage charges are not cancelled.'
                : 'Restore serving access after propagation. This does not change authentication or row policies.'}
          </p>
          <p>
            <strong>{p.name}</strong>
          </p>
          {action === 'delete' && (
            <div className="field">
              <label htmlFor="delete-project-name">Type the project name to confirm</label>
              <input
                id="delete-project-name"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                autoComplete="off"
              />
            </div>
          )}
          {error && <ErrorBox>{error}</ErrorBox>}
          <div className="modal-actions">
            <button className="btn" type="button" disabled={busy} onClick={() => setAction(null)}>
              Cancel
            </button>
            <button
              className={`btn ${action === 'delete' ? 'btn-danger' : 'btn-primary'}`}
              disabled={busy || (action === 'delete' && confirmation !== p.name)}
            >
              {busy ? 'Working…' : action === 'delete' ? 'Delete permanently' : 'Confirm'}
            </button>
          </div>
        </form>
      </Dialog>
    </>
  )
}
